"use client"

import { useCallback, useEffect, useState } from "react"
import api from "../utils/api"
import Icon from "./Icon"
import { formatMoney } from "../utils/format"
import { getApiErrorMessage } from "../utils/errorMessage"
import { GLOBAL_WALLET_MODULES } from "./GlobalWalletCard"
import AsyncActionModal from "./AsyncActionModal"
import PrepayForm from "./PrepayForm"

const MODULE_LABELS = GLOBAL_WALLET_MODULES.reduce((acc, m) => {
  acc[m.key] = m.label
  return acc
}, {})

// Global wallet settings: transfer funds between module wallets and, for
// disbursement-eligible users, choose which wallets feed disbursements.
function GlobalWalletSettingsModal({ onClose, onBalancesChanged }) {
  const [activeSection, setActiveSection] = useState("transfer")
  const [summary, setSummary] = useState(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState("")

  // Transfer form state
  const [fromWallet, setFromWallet] = useState("frp")
  const [toWallet, setToWallet] = useState("groups")
  const [amount, setAmount] = useState("")
  const [transferLoading, setTransferLoading] = useState(false)
  const [transferError, setTransferError] = useState("")
  const [transferSuccess, setTransferSuccess] = useState("")
  const [transferDetails, setTransferDetails] = useState(null)

  // Disbursement settings state
  const [disbSettings, setDisbSettings] = useState(null)
  const [disbSources, setDisbSources] = useState(["frp"])
  const [disbLoading, setDisbLoading] = useState(false)
  const [disbSaving, setDisbSaving] = useState(false)
  const [disbError, setDisbError] = useState("")
  const [disbSuccess, setDisbSuccess] = useState("")

  const fetchSummary = useCallback(async () => {
    try {
      setSummaryError("")
      const response = await api.get("/wallet/global/summary")
      setSummary(response?.data || null)
    } catch (error) {
      console.error("Error fetching global wallet summary:", error)
      setSummaryError(getApiErrorMessage(error, "Failed to load wallet balances."))
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const fetchDisbursementSettings = useCallback(async () => {
    try {
      setDisbLoading(true)
      setDisbError("")
      const response = await api.get("/wallet/global/disbursement-settings")
      setDisbSettings(response?.data || null)
      setDisbSources(
        Array.isArray(response?.data?.sources) && response.data.sources.length > 0
          ? response.data.sources
          : ["frp"]
      )
    } catch (error) {
      console.error("Error fetching disbursement settings:", error)
      setDisbError(getApiErrorMessage(error, "Failed to load disbursement settings."))
    } finally {
      setDisbLoading(false)
    }
  }, [])

  useEffect(() => {
    if (activeSection === "disbursements" && !disbSettings && !disbLoading) {
      fetchDisbursementSettings()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  const moduleBalance = (key) => {
    const modules = Array.isArray(summary?.modules) ? summary.modules : []
    return Number(modules.find((m) => m.key === key)?.balance || 0)
  }

  const handleFromChange = (value) => {
    setFromWallet(value)
    if (value === toWallet) {
      const fallback = GLOBAL_WALLET_MODULES.find((m) => m.key !== value)
      if (fallback) setToWallet(fallback.key)
    }
  }

  const swapWallets = () => {
    setFromWallet(toWallet)
    setToWallet(fromWallet)
  }

  const parsedAmount = parseFloat(amount)
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0
  const sourceBalance = moduleBalance(fromWallet)
  const exceedsBalance = amountValid && parsedAmount > sourceBalance

  const handleTransfer = async (e) => {
    e.preventDefault()
    if (!amountValid || exceedsBalance || fromWallet === toWallet || transferLoading) return

    const snapshot = { from: fromWallet, to: toWallet, amount: parsedAmount }

    try {
      setTransferLoading(true)
      setTransferError("")
      setTransferSuccess("")
      const response = await api.post("/wallet/global/transfer", {
        from: snapshot.from,
        to: snapshot.to,
        amount: snapshot.amount,
      })
      if (Array.isArray(response?.data?.modules)) {
        setSummary((prev) => ({
          ...(prev || {}),
          modules: response.data.modules,
          total: response.data.total,
        }))
      } else {
        fetchSummary()
      }
      setAmount("")
      setTransferDetails(snapshot)
      setTransferSuccess(
        `${formatMoney(snapshot.amount)} moved from ${MODULE_LABELS[snapshot.from]} to ${MODULE_LABELS[snapshot.to]}.`
      )
      if (typeof onBalancesChanged === "function") onBalancesChanged()
    } catch (error) {
      console.error("Error transferring funds:", error)
      setTransferDetails(snapshot)
      setTransferError(getApiErrorMessage(error, "Failed to transfer funds."))
    } finally {
      setTransferLoading(false)
    }
  }

  const closeTransferModal = () => {
    setTransferError("")
    setTransferSuccess("")
    setTransferDetails(null)
  }

  const toggleDisbSource = (key) => {
    if (key === "frp") return // Rentals wallet is always the payout wallet
    setDisbSuccess("")
    setDisbSources((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    )
  }

  const handleDisbSave = async () => {
    if (disbSaving) return
    try {
      setDisbSaving(true)
      setDisbError("")
      setDisbSuccess("")
      const response = await api.put("/wallet/global/disbursement-settings", {
        sources: disbSources,
      })
      if (Array.isArray(response?.data?.sources)) {
        setDisbSources(response.data.sources)
      }
      setDisbSuccess("Disbursement settings saved.")
    } catch (error) {
      console.error("Error saving disbursement settings:", error)
      setDisbError(getApiErrorMessage(error, "Failed to save disbursement settings."))
    } finally {
      setDisbSaving(false)
    }
  }

  const canManageDisbursements = summary?.canManageDisbursements === true

  const sections = [
    { key: "transfer", label: "Transfer Funds", icon: "arrow-left-right" },
    { key: "prepay", label: "Prepay a Company", icon: "arrow-up-right-circle" },
    ...(canManageDisbursements
      ? [{ key: "disbursements", label: "Disbursements", icon: "bank" }]
      : []),
  ]

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4 flex-shrink-0">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Global Wallet Settings</h3>
            <p className="mt-0.5 text-xs text-gray-500">
              Manage funds across your Rentals, Groups and Utilities wallets.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <Icon name="x" size="1.2rem" />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row flex-1 min-h-0">
          {/* Sidebar (horizontal tabs on mobile) */}
          <div className="flex sm:flex-col gap-1 border-b sm:border-b-0 sm:border-r border-gray-100 bg-gray-50/60 px-3 py-2 sm:py-4 sm:w-48 flex-shrink-0 overflow-x-auto">
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => setActiveSection(section.key)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeSection === section.key
                    ? "bg-white text-emerald-700 shadow-sm border border-emerald-100"
                    : "text-gray-600 hover:bg-white hover:text-gray-800"
                }`}
              >
                <Icon name={section.icon} size="0.9rem" />
                {section.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {summaryLoading ? (
              <p className="text-sm text-gray-500 py-8 text-center">Loading wallet balances...</p>
            ) : summaryError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-700 text-sm">{summaryError}</p>
              </div>
            ) : activeSection === "transfer" ? (
              <form onSubmit={handleTransfer}>
                {/* Balances overview */}
                <div className="grid grid-cols-3 gap-2 mb-5">
                  {GLOBAL_WALLET_MODULES.map((m) => (
                    <div key={m.key} className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{m.label}</p>
                      <p className="text-sm font-bold text-gray-800 tabular-nums">
                        {formatMoney(moduleBalance(m.key))}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                    <select
                      value={fromWallet}
                      onChange={(e) => handleFromChange(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {GLOBAL_WALLET_MODULES.map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.label} Wallet — {formatMoney(moduleBalance(m.key))}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={swapWallets}
                      aria-label="Swap wallets"
                      className="flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700"
                    >
                      <Icon name="arrow-down-up" size="0.75rem" />
                      Swap
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <select
                      value={toWallet}
                      onChange={(e) => setToWallet(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    >
                      {GLOBAL_WALLET_MODULES.filter((m) => m.key !== fromWallet).map((m) => (
                        <option key={m.key} value={m.key}>
                          {m.label} Wallet — {formatMoney(moduleBalance(m.key))}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value)
                          setTransferSuccess("")
                        }}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Enter amount"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setAmount(String(sourceBalance || ""))}
                        disabled={!(sourceBalance > 0)}
                        className="px-3 py-2 text-xs font-semibold rounded-lg border border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        Max
                      </button>
                    </div>
                    {exceedsBalance && (
                      <p className="mt-1 text-xs text-red-600">
                        Amount exceeds your {MODULE_LABELS[fromWallet]} Wallet balance of {formatMoney(sourceBalance)}.
                      </p>
                    )}
                  </div>

                  {amountValid && !exceedsBalance && (
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      You are moving <span className="font-bold">{formatMoney(parsedAmount)}</span> from{" "}
                      <span className="font-bold">{MODULE_LABELS[fromWallet]}</span> to{" "}
                      <span className="font-bold">{MODULE_LABELS[toWallet]}</span>.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={!amountValid || exceedsBalance || transferLoading}
                    className="w-full bg-emerald-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {transferLoading ? "Processing..." : "Transfer Funds"}
                  </button>
                </div>
              </form>
            ) : activeSection === "prepay" ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Prepay a billing company</h4>
                  <p className="mt-1 text-xs text-gray-500">
                    Send money from your Utilities wallet to a company before it bills you. The money
                    can only pay that company&apos;s bills. Your normal automatic bill payment does not
                    change.
                  </p>
                </div>
                <PrepayForm
                  onDone={() => {
                    fetchSummary()
                    if (typeof onBalancesChanged === "function") onBalancesChanged()
                  }}
                />
              </div>
            ) : (
              <div>
                {disbLoading ? (
                  <p className="text-sm text-gray-500 py-8 text-center">Loading disbursement settings...</p>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">Disbursement source wallets</h4>
                      <p className="mt-1 text-xs text-gray-500">
                        Disbursements are paid to your bank from the Rentals wallet. Wallets selected
                        below are moved into the Rentals wallet automatically each time a disbursement runs.
                      </p>
                    </div>

                    {disbSettings?.bank?.bankName || disbSettings?.bank?.bankAccNumberMasked ? (
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                        Payout destination:{" "}
                        <span className="font-semibold text-gray-800">
                          {disbSettings?.bank?.bankName || "Bank"}
                        </span>
                        {disbSettings?.bank?.bankAccNumberMasked ? ` (${disbSettings.bank.bankAccNumberMasked})` : ""}
                      </div>
                    ) : (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        No bank details set. Add your bank name and account number in Profile so
                        disbursements can be sent.
                      </div>
                    )}

                    <div className="space-y-2">
                      {GLOBAL_WALLET_MODULES.map((m) => {
                        const checked = disbSources.includes(m.key)
                        const locked = m.key === "frp"
                        return (
                          <label
                            key={m.key}
                            className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${
                              locked
                                ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                                : "border-gray-200 bg-white cursor-pointer hover:border-emerald-200"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={locked}
                              onChange={() => toggleDisbSource(m.key)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                            />
                            <span className="flex-1">
                              <span className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                                <Icon name={m.icon} size="0.8rem" />
                                {m.label} Wallet
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                {m.key === "frp"
                                  ? "Always included — this is the wallet disbursements are paid out from."
                                  : m.key === "groups"
                                    ? "Include your Groups wallet balance in disbursements."
                                    : "Include your Utilities wallet balance in disbursements."}
                              </span>
                            </span>
                          </label>
                        )
                      })}
                    </div>

                    <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-[11px] text-gray-500">
                      Auto disburse is currently{" "}
                      <span className={`font-semibold ${disbSettings?.autoDisburse ? "text-emerald-700" : "text-gray-700"}`}>
                        {disbSettings?.autoDisburse ? "ON" : "OFF"}
                      </span>
                      . You can toggle it on the Rentals module card on your dashboard.
                    </div>

                    {disbError && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-red-700 text-sm">{disbError}</p>
                      </div>
                    )}
                    {disbSuccess && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <p className="text-emerald-700 text-sm">{disbSuccess}</p>
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleDisbSave}
                      disabled={disbSaving}
                      className="w-full bg-emerald-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    >
                      {disbSaving ? "Saving..." : "Save Disbursement Settings"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    <AsyncActionModal
      open={transferLoading || !!transferError || !!transferSuccess}
      status={transferLoading ? "loading" : transferError ? "error" : "success"}
      title={transferLoading ? "Processing..." : transferError ? "Transfer Failed" : "Transfer Successful"}
      statusLabel={transferLoading ? "Processing..." : transferError ? "Transfer Failed" : "Transfer Successful"}
      message={transferLoading ? "Please wait while we move your funds." : transferError || transferSuccess}
      details={
        !transferLoading && transferDetails
          ? [
              { label: "Amount", value: formatMoney(transferDetails.amount) },
              { label: "From", value: `${MODULE_LABELS[transferDetails.from]} Wallet` },
              { label: "To", value: `${MODULE_LABELS[transferDetails.to]} Wallet` },
            ]
          : []
      }
      onClose={closeTransferModal}
      primaryLabel={transferLoading ? undefined : "Done"}
      onPrimary={closeTransferModal}
    />
    </>
  )
}

export default GlobalWalletSettingsModal
